import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BuilderHint from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderHint';
import { TextField } from '@scale/scaleui';
import { omitBy, isUndefined, debounce } from 'lodash';

interface Props {
  name: string;
  updateProperty: (value: any) => void;
  existingValue?: number;
  required?: boolean;
  hint?: string;
  disabled?: boolean;
  error?: boolean;
  step?: number;
  min?: number;
  max?: number;
}

const NumberInputBuilder: React.FC<Props> = ({
  name,
  updateProperty,
  existingValue,
  required,
  hint,
  disabled,
  error,
  step,
  min,
  max,
}: Props) => {
  const [valid, setValid] = useState<boolean>(false);
  const [value, setValue] = useState<number | undefined>(existingValue);
  const [outOfBounds, setOutOfBounds] = useState<boolean>(false);

  const textFieldInputRef = useRef<HTMLInputElement>(null);
  // Remove scrolling incrementing and decrementing inputs of type number
  useEffect(() => {
    const input = textFieldInputRef.current;

    const handleWheel = (e: Event) => {
      e.preventDefault();
    };

    if (input) {
      // Scrolling is default passive, need to make active to be able to preventDefault
      input.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (input) {
        input.removeEventListener('wheel', handleWheel);
      }
    };
  }, []);

  useEffect(() => {
    if (existingValue) {
      const existingValueNumber = Number(existingValue);

      // explicitly check min is not undefined to prevent confusion for cases where min is 0
      if (min !== undefined && !isNaN(existingValueNumber) && existingValueNumber < min) {
        setOutOfBounds(true);
        return;
      }

      if (max !== undefined && !isNaN(existingValueNumber) && existingValueNumber > max) {
        setOutOfBounds(true);
        return;
      }
    }

    setOutOfBounds(false);
    setValue(existingValue);
  }, [existingValue, min, max]);

  useEffect(() => {
    let valid = outOfBounds;
    if (error !== undefined) {
      valid = valid && error;
    }

    setValid(valid);
  }, [outOfBounds, error]);

  const debouncedUpdateProperty = useCallback(
    debounce((value: any) => updateProperty(value), 500),
    [updateProperty],
  );

  const props = useMemo(
    () =>
      omitBy(
        {
          step,
          min,
          max,
          disabled: !!disabled,
          error: valid,
          required,
        },
        isUndefined,
      ),
    [step, min, max, disabled, valid, required],
  );

  return (
    <div className="flex items-center">
      <TextField
        {...props}
        id={name}
        value={value}
        label={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (!required && e.target.value === '') {
            setValue(undefined); // allows number field to be empty
            debouncedUpdateProperty(undefined);
          } else {
            setValue(Number(e.target.value));
            debouncedUpdateProperty(Number(e.target.value));
          }
        }}
        onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (!required && e.target.value === '') {
            debouncedUpdateProperty(undefined);
          } else {
            debouncedUpdateProperty(Number(e.target.value));
          }
        }}
        type="number"
        ref={textFieldInputRef}
      />
      {hint && <BuilderHint name={name} hint={hint} />}
      {outOfBounds && (
        <p className="pl-5 text-danger-500">
          Value must be between {min} and {max}.
        </p>
      )}
    </div>
  );
};

export default NumberInputBuilder;
